<template>
  <div class="space-y-6 p-4 flex ml-auto max-h-content w-screen min-h-content max-w-[98%]">
    <div class="h-full overflow-y-auto w-full flex flex-col">
      <!-- Main Dashboard Grid -->
      <div class="grid grid-cols-4 gap-2 mb-2">
        <!-- Left column: Project Status + Details -->
        <div class="flex flex-col gap-2 h-full">
          <!-- Project Overview Card -->
          <div class="h-full">
            <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
              <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
                <div class="flex items-center justify-between">
                  <h2 class="font-semibold text-white">
                    {{ $t('ProjectSelector.ClientDashboard.projectStatus') }}
                  </h2>

                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                  </div>
                </div>
              </div>

              <div class="p-6 flex justify-center h-full">
                <Transition
                  enter-active-class="animate__animated animate__fadeIn animate__fast"
                  mode="out-in"
                >
                  <table
                    v-if="project"
                    class="text-sm w-full"
                  >
                    <tbody class="child:border-b child:border-gray-100 last-child:border-b-0">
                      <tr class="child:py-1">
                        <td class="font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.startDate') }}
                        </td>

                        <td class="text-right font-semibold text-gray-900">
                          {{ project.contract_start_date ? project.contractStartDate.toLocaleDateString() : $t('ProjectSelector.ClientDashboard.notSet') }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.endDate') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-gray-900">
                          {{ project.contract_end_date ? project.contractEndDate.toLocaleDateString() : $t('ProjectSelector.ClientDashboard.notSet') }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.duration') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-gray-900">
                          {{ project.contract_start_date && project.contract_end_date ? `${project.contractLengthInWeeks} ${$t('ProjectSelector.ClientDashboard.weeks')}` : $t('ProjectSelector.ClientDashboard.notSet') }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.approvedDelays') }}
                          <span class="ml-1 h-5 w-5 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{{ clientMetrics.approved_delays_count }}</span>
                        </td>

                        <td class="text-right text-sm font-semibold text-green-600">
                          {{ clientMetrics.approved_delays_days }} {{ $t('ProjectSelector.ClientDashboard.days') }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.approvedContractEndDate') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-blue-600">
                          {{ project.contract_end_date ? project.estimateContractEndDate(clientMetrics.approved_delays_days).toJSDate().toLocaleDateString() : $t('ProjectSelector.ClientDashboard.notSet') }}
                        </td>
                      </tr>

                      <tr class=" child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.contractValue') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-blue-600">
                          {{ australianCurrency(totalBudget) }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.approvedVariationsValue') }}
                          <span class="ml-1 h-5 w-5 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{{ clientMetrics.approved_variations_count }}</span>
                        </td>

                        <td class="text-right text-sm font-semibold text-blue-600">
                          {{ australianCurrency(clientMetrics.approved_variations_budget) }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.finalAccount') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-red-600">
                          {{ australianCurrency(totalBudget + clientMetrics.approved_variations_budget) }}
                        </td>
                      </tr>

                      <tr class="child:py-1">
                        <td class="text-sm font-medium text-gray-600">
                          {{ $t('ProjectSelector.ClientDashboard.paymentsCertified') }}
                        </td>

                        <td class="text-right text-sm font-semibold text-orange-600">
                          {{ australianCurrency(claimCertifiedTotal) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Contractor & Site Details Card -->
        <div
          v-if="hasContractorOrSiteDetails"
          class="bg-white rounded-xl shadow-sm"
        >
          <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-white">
                {{ $t('ProjectSelector.ClientDashboard.projectDetails') }}
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-white">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
</svg>
              </div>
            </div>
          </div>

          <div class="p-6 space-y-4">
            <div
              v-if="contractorDetails"
            >
              <h3 class="text-sm font-medium text-gray-600 mb-1">
                {{ $t('ProjectSelector.ClientDashboard.contractorDetails') }}
              </h3>

              <p class="text-sm font-semibold text-gray-900 whitespace-pre-wrap">
                {{ contractorDetails }}
              </p>
            </div>

            <div
              v-if="siteDetails"
            >
              <h3 class="text-sm font-medium text-gray-600 mb-1">
                {{ $t('ProjectSelector.ClientDashboard.siteDetails') }}
              </h3>

              <p class="text-sm font-semibold text-gray-900 whitespace-pre-wrap">
                {{ siteDetails }}
              </p>
            </div>
          </div>
        </div>

        <!-- Development Costs Card -->
        <div
          v-if="indirectTradesTotalBudget > 0"
          class="h-full"
        >
          <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
            <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
              <div class="flex items-center justify-between">
                <h2 class="font-semibold text-white">
                  {{ $t('ProjectSelector.ClientDashboard.developmentCosts') }}
                </h2>

                <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
                </div>
              </div>
            </div>

            <div class="p-6 flex flex-col h-full">
              <Transition
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                mode="out-in"
              >
                <div
                  v-if="indirectTradesTotalBudget > 0"
                  class="flex flex-col h-full"
                >
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center space-x-4">
                      <div class="flex items-center space-x-2">
                        <div class="w-3 h-3 bg-blue-500 rounded-full" />

                        <span class="text-sm text-gray-600 font-medium">{{ $t('ProjectSelector.ClientDashboard.certified') }}</span>
                      </div>

                      <div class="flex items-center space-x-2">
                        <div class="w-3 h-3 bg-red-500 rounded-full" />

                        <span class="text-sm text-gray-600 font-medium">{{ $t('ProjectSelector.ClientDashboard.remaining') }}</span>
                      </div>
                    </div>
                  </div>

                  <IndirectCostsPieChart
                    :total-budget="indirectTradesTotalBudget"
                    :total-certified="indirectTradesTotalCertified"
                  />

                  <div class="mt-auto">
                    <div class="flex justify-between items-center py-2 border-b border-gray-100">
                      <span class="text-sm font-medium text-gray-600">
                        {{ $t('ProjectSelector.ClientDashboard.indirectTradesTotalCertified') }}
                      </span>

                      <span class="text-sm font-semibold text-blue-600">
                        {{ australianCurrency(indirectTradesTotalCertified) }}
                      </span>
                    </div>

                    <div class="flex justify-between items-center py-2">
                      <span class="text-sm font-medium text-gray-600">
                        {{ $t('ProjectSelector.ClientDashboard.indirectTradesTotalBudget') }}
                      </span>

                      <span class="text-sm font-semibold text-red-600">
                        {{ australianCurrency(indirectTradesTotalBudget) }}
                      </span>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>

        <!-- Work Breakdown Card -->
        <div
          class="h-full"
          :class="{ 'lg:col-span-2': indirectTradesTotalBudget === 0 }"
        >
          <div class="bg-white rounded-xl shadow-sm">
            <div class="rounded-t-xl bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5">
              <div class="flex items-center justify-between">
                <h2 class="text font-semibold text-white">
                  {{ $t('ProjectSelector.ClientDashboard.workBreakdown') }}
                </h2>

                <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
                </div>
              </div>
            </div>

            <div class="p-2">
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center space-x-4 px-2">
                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-blue-500 rounded-full" />

                    <span class="text-sm text-gray-600 font-medium">{{ $t('ProjectSelector.ClientDashboard.workDone') }}</span>
                  </div>

                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-red-500 rounded-full" />

                    <span class="text-sm text-gray-600 font-medium">{{ $t('ProjectSelector.ClientDashboard.remaining') }}</span>
                  </div>
                </div>

                <div class="text-right">
                  <div class="text font-bold text-gray-900">
                    {{ australianCurrency(currentCertifiedAmount) }}
                  </div>

                  <div class="text-xs text-gray-500">
                    {{ $t('ProjectSelector.ClientDashboard.of') }} {{ australianCurrency(totalBudget + variationOrdersTotalApproved) }}
                  </div>
                </div>
              </div>

              <WorkBreakdownPieChart
                :cumulative-amounts="monthlyCertifiedCumulative"
                :total-budget="totalBudget"
                :approved-variations-budget="variationOrdersTotalApproved"
                :hovered-index="hoveredIndex"
              />

              <!-- Progress Stats -->
              <div class="mt-6 grid grid-cols-2 gap-4">
                <div class="bg-blue-50 rounded-lg p-4">
                  <div class="text-sm text-blue-600 font-medium">
                    {{ $t('ProjectSelector.ClientDashboard.completion') }}
                  </div>

                  <div class="text-xl font-bold text-blue-900">
                    {{ totalBudget + variationOrdersTotalApproved > 0 ? ((currentCertifiedAmount / (totalBudget + variationOrdersTotalApproved)) * 100).toFixed(1) : '0' }}%
                  </div>
                </div>

                <div class="bg-red-50 rounded-lg p-4">
                  <div class="text-sm text-red-600 font-medium">
                    {{ $t('ProjectSelector.ClientDashboard.remaining') }}
                  </div>

                  <div class="text-xl font-bold text-red-900">
                    {{ $tcc(Math.max(remainingBudget, 0)) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Budget Timeline Chart Section -->
      <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
        <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
          <div class="flex items-center justify-between">
            <h2 class="font-semibold text-white">
              {{ $t('ProjectSelector.ClientDashboard.projectProgressTimeline') }}
            </h2>

            <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
</svg>
            </div>
          </div>
        </div>

        <div class="p-2 h-full">
          <ProjectProgressTimelineGraph
            v-if="project && project.contract_start_date && project.contract_end_date"
            :project="project"
            :monthly-certified-cumulative="monthlyCertifiedCumulative"
            :baseline-cumulative="baselineCumulative"
            :forecast-cumulative="forecastCumulative"
            :extended-chart-months="extendedChartMonths"
            @hover="handleTimelineHover"
            @mouseleave="handleTimelineHover(undefined)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IndirectCostsPieChart from '@/components/HeadContract/Dashboard/IndirectCostsPieChart.vue';
import WorkBreakdownPieChart from '@/components/ProjectSelector/ClientDashboard/WorkBreakdownPieChart.vue';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum } from '@/helpers';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import ProjectProgressTimelineGraph from './ClientDashboard/ProjectProgressTimelineGraph.vue';

const projectsStore = useProjectsStore();
// Track hovered index for pie chart synchronization
const hoveredIndex = ref<number | undefined>(undefined);

const handleTimelineHover = (index: number | undefined) => {
  hoveredIndex.value = index;
};

const project = computed(() => {
  return projectsStore.project;
});

const contractorDetails = computed(() => {
  return project.value?.head_contract_settings?.hired_company_details?.trim() || '';
});

const siteDetails = computed(() => {
  return project.value?.head_contract_settings?.site_details?.trim() || '';
});

const hasContractorOrSiteDetails = computed(() => {
  return Boolean(contractorDetails.value || siteDetails.value);
});

// Client metrics data
const clientMetrics = ref<ClientMetricsSummary>({
  total_budget: 0,
  approved_variations_budget: 0,
  total_certified: 0,
  remaining_budget: 0,
  contract_length_in_weeks: 0,
  forecast: [],
  baseline: [],
  actuals: [],
  latest_claim_date: undefined,
  approved_variations_count: 0,
  approved_delays_count: 0,
  approved_delays_days: 0,
  indirect_trades_total_budget: 0,
  indirect_trades_total_certified: 0,
});

const fetchClientMetricsAction = useStoreApiAction(projectsStore.clientMetrics);

// Fetch client metrics when project changes
watch(() => project.value?.id, (newValue) => {
  if(!newValue) return;

  fetchClientMetricsAction.request(newValue).then((data) => {
    clientMetrics.value = data as ClientMetricsSummary;
  }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

// Use client metrics data for calculations
const totalBudget = computed(() => {
  return clientMetrics.value?.total_budget || 0;
});

const claimCertifiedTotal = computed(() => {
  return clientMetrics.value?.total_certified || 0;
});

const variationOrdersTotalApproved = computed(() => {
  return clientMetrics.value.approved_variations_budget;
});

const monthlyCertifiedCumulative = computed(() => {
  return cumulativeSum(clientMetrics.value.actuals);
});

const baselineCumulative = computed(() => {
  return cumulativeSum(clientMetrics.value.baseline);
});

const forecastCumulative = computed(() => {
  return cumulativeSum(clientMetrics.value.forecast);
});

const currentCertifiedAmount = computed(() => {
  const index = hoveredIndex.value !== undefined ? hoveredIndex.value : (monthlyCertifiedCumulative.value.length - 1);

  return monthlyCertifiedCumulative.value[index] || 0;
});

const remainingBudget = computed(() => {
  return totalBudget.value + variationOrdersTotalApproved.value - currentCertifiedAmount.value;
});

const indirectTradesTotalBudget = computed(() => {
  return clientMetrics.value.indirect_trades_total_budget || 0;
});

const indirectTradesTotalCertified = computed(() => {
  return clientMetrics.value.indirect_trades_total_certified || 0;
});

const extendedChartMonths = computed(() => {
  if(!project.value) {
    return [];
  }

  const lastClaim = clientMetrics.value.latest_claim_date;
  const contractStart = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');
  const contractEnd = DateTime.fromJSDate(project.value.contractEndDate).endOf('month');

  if(lastClaim) {
    const lastClaimEnd = DateTime.fromISO(lastClaim).endOf('month');
    const chartEnd = lastClaimEnd > contractEnd ? lastClaimEnd : contractEnd;
    const months = [];
    let currentMonth = contractStart;

    while(currentMonth <= chartEnd) {
      months.push(currentMonth);
      currentMonth = currentMonth.plus({ months: 1 });
    }

    return months;
  }

  return project.value.contractMonths;
});
</script>

<style scoped></style>
